import {
  Box,
  BoxProps,
  SxProps,
  Table,
  TableContainer,
  Typography,
} from "@mui/material";
import { cloneElement, memo, useEffect, useRef, useState } from "react";
import { CustomButton } from "../customButton/CustomButton";
import { TablePaginationProps } from "../pagination/types";
import { SortFieldProps } from "./SortField";
import { TableBodyStripedRows } from "./styled";
import TableHeader from "./TableHeader";
import { StripedTableHeader } from "./types";

const COLLAPSE_DURATION_MS = 150;

export interface TableAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  sx?: SxProps;
}

// TODO: change headers to be array of objects (header props, align etc.) or string[]
interface Props {
  columnHeaders: string[] | StripedTableHeader[];
  itemRows: JSX.Element[];
  pagination?: TablePaginationProps;
  sortProps?: SortFieldProps;
  updateSelectedIds?: (ids: string[]) => void;
  selectableRows?: boolean;
  selectedIds?: string[];
  /**
   * IDs that cannot be selected. Skipped in merge-mode select-all and allSelected check.
   * Row components handle their own disabled visual state independently.
   */
  nonSelectableIds?: string[];
  /**
   * "replace": select-all replaces the whole selection with the current page (default,
   * preserves existing BulkSalaryIncrease behaviour).
   * "merge": select-all adds/removes only current page ids, preserving other-page selections.
   */
  selectAllMode?: "replace" | "merge";
  // use this if you need to use custom rules for the stripes in the row component
  // e.g. if rows includes nested collapsible rows
  disableStripes?: boolean;
  disableRowClick?: boolean;
  headerSize?: "small" | "medium";
  maxWidth?: string;
  tableAction?: TableAction;
  infoText?: string;
  infoTextContainerProps?: BoxProps;
  /**
   * When using isCollapsed and onToggleCollapse, columnHeaders must use StripedTableHeader[]
   * with percentage-based widths that sum to exactly 100%.
   * Do NOT mix percentage and fixed (px) widths — fixed widths cause overflow
   * in table-layout: fixed mode.
   */
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const StripedTableComponent = ({
  columnHeaders,
  itemRows,
  pagination,
  sortProps,
  updateSelectedIds,
  selectableRows = false,
  selectedIds,
  nonSelectableIds,
  selectAllMode = "replace",
  disableStripes = false,
  disableRowClick = false,
  headerSize = "medium",
  maxWidth = "unset",
  tableAction,
  infoText,
  infoTextContainerProps = {},
  isCollapsed,
  onToggleCollapse,
}: Props) => {
  const [isBodyVisible, setIsBodyVisible] = useState(!isCollapsed);
  const [bodyOpacity, setBodyOpacity] = useState(isCollapsed ? 0 : 1);
  const animationTimer = useRef<NodeJS.Timeout>();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!onToggleCollapse) return;

    clearTimeout(animationTimer.current);

    if (isCollapsed) {
      setBodyOpacity(0);
      animationTimer.current = setTimeout(
        () => setIsBodyVisible(false),
        COLLAPSE_DURATION_MS,
      );
    } else {
      setIsBodyVisible(true);
      animationTimer.current = setTimeout(() => setBodyOpacity(1), 16);
    }

    return () => clearTimeout(animationTimer.current);
  }, [isCollapsed, onToggleCollapse]);

  const selectablePageIds = itemRows
    .map((row) => row.props.id as string)
    .filter((id) => !nonSelectableIds?.includes(id));

  const allPageSelected =
    selectablePageIds.length > 0 &&
    selectablePageIds.every((id) => selectedIds?.includes(id));

  const somePageSelected = selectablePageIds.some((id) =>
    selectedIds?.includes(id),
  );

  const indeterminate = somePageSelected && !allPageSelected;

  const toggleSelectAll = () => {
    if (!selectedIds || !updateSelectedIds) return;

    if (selectAllMode === "merge") {
      if (allPageSelected) {
        updateSelectedIds(
          selectedIds.filter((id) => !selectablePageIds.includes(id)),
        );
      } else {
        updateSelectedIds([
          ...new Set([...selectedIds, ...selectablePageIds]),
        ]);
      }
    } else {
      // replace mode: existing behaviour, using props.id consistently
      if (selectedIds.length === itemRows.length) {
        updateSelectedIds([]);
      } else {
        updateSelectedIds(itemRows.map((row) => row.props.id as string));
      }
    }
  };

  const toggleSelectRow = (id: string) => {
    if (selectedIds && updateSelectedIds) {
      const newSelected = selectedIds.includes(id)
        ? selectedIds.filter((prevId) => prevId !== id)
        : [...selectedIds, id];

      updateSelectedIds(newSelected);
    }
  };

  const isSelected = (id: string) => selectedIds && selectedIds.includes(id);

  const resolvedRows = selectableRows
    ? itemRows.map((itemRow) =>
        cloneElement(itemRow, {
          isSelected: isSelected(itemRow.props.id),
          onSelect: () => toggleSelectRow(itemRow.props.id),
        }),
      )
    : itemRows;

  return (
    <>
      {(tableAction || infoText) && (
        <Box display="flex" alignItems="center" gap="16px">
          {tableAction && (
            <CustomButton
              disabled={tableAction.disabled}
              variant="contained"
              sx={{
                whiteSpace: "nowrap",
                height: "42px",
                borderRadius: "24px",
                margin: "8px 0px 24px 0px",
                ...tableAction.sx,
              }}
              onClick={tableAction.onClick}
            >
              {tableAction.label}
            </CustomButton>
          )}
          {infoText && (
            <Box
              sx={{ margin: "8px 0px 24px 8px", maxWidth: "800px" }}
              {...infoTextContainerProps}
            >
              <Typography variant="secondary">{infoText}</Typography>
            </Box>
          )}
        </Box>
      )}
      <TableContainer sx={{ maxWidth }}>
        <Table
          sx={
            onToggleCollapse
              ? { tableLayout: "fixed", width: "100%" }
              : undefined
          }
        >
          <TableHeader
            columnHeaders={columnHeaders}
            pagination={pagination}
            sortProps={sortProps}
            toggleSelectAll={toggleSelectAll}
            allSelected={allPageSelected}
            indeterminate={indeterminate}
            selectableRows={selectableRows}
            size={headerSize}
            isCollapsed={isCollapsed}
            onToggleCollapse={onToggleCollapse}
          />
          <TableBodyStripedRows
            disableStripes={disableStripes}
            disableRowClick={disableRowClick}
            sx={
              onToggleCollapse
                ? {
                    display: isBodyVisible ? "table-row-group" : "none",
                    opacity: bodyOpacity,
                    transition: `opacity ${COLLAPSE_DURATION_MS}ms ease-in-out`,
                  }
                : undefined
            }
          >
            {resolvedRows}
          </TableBodyStripedRows>
        </Table>
      </TableContainer>
    </>
  );
};

export const StripedTable = memo(StripedTableComponent);
